import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus } from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { useTeam } from "../contexts/TeamContext";
import { api } from "../lib/chalkApi";
import {
  ChalkCard, ChalkIllustrationLines, DarkHeader, EmptyState,
  SectionTitle, SportBadge, WhiteHeader,
} from "../components/chalk/primitives";
import AddEventSheet from "../components/chalk/AddEventSheet";
import EventChangeSheet from "../components/chalk/EventChangeSheet";
import BulkRedrawSheet from "../components/chalk/BulkRedrawSheet";
import EventWeatherBadge from "../components/chalk/EventWeatherBadge";
import TeamLockBanner from "../components/chalk/TeamLockBanner";
import { startCheckout } from "../lib/checkout";
import { eventSortKey, formatEventWhen } from "../lib/eventTime";
import { toast } from "sonner";

function humanWhen(event, user) {
  // Timezone-aware. Renders in the user's preferred zone if set, else the
  // team's zone. Matches Chalkboard / NextPlayCard / FamilyDashboard so
  // coach and parent see identical wall-clock times.
  if (!event) return "";
  const base = formatEventWhen(event, user);
  if (!base) return "";
  const d = new Date(event.utc_time || event.start_utc || event.local_time);
  const today = new Date();
  const tomorrow = new Date(); tomorrow.setDate(today.getDate() + 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  // Strip the leading weekday/date for relative-day prefixing.
  if (same(d, today)) {
    const time = base.split("·").slice(1).join("·").trim();
    return `Today · ${time}`;
  }
  if (same(d, tomorrow)) {
    const time = base.split("·").slice(1).join("·").trim();
    return `Tomorrow · ${time}`;
  }
  return base;
}

export default function Playbook() {
  const { user } = useAuth();
  const { activeTeam, teams } = useTeam();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [changing, setChanging] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const canAdd = user.role !== "guardian";
  const isLocked = !!(activeTeam && activeTeam.is_locked);

  function tryAdd() {
    if (isLocked) {
      startCheckout({
        tier: "chalkboard",
        teamId: activeTeam.id,
        context: { feature_name: "MULTI_TEAM", source_screen: "playbook_fab", current_tier: "pickup" },
      }).catch((e) => toast.error(e.message || "Couldn't open checkout."));
      return;
    }
    setAddOpen(true);
  }

  async function reloadEvents() {
    if (activeTeam) {
      const data = await api.listEvents(activeTeam.id);
      setEvents((data.events || []).map((e) => ({ ...e, team_name: activeTeam.name, team_sport: activeTeam.sport })));
    }
  }

  useEffect(() => {
    async function load() {
      if (user.role === "guardian") {
        // Aggregate across guardian's teams
        const all = [];
        for (const t of teams) {
          try {
            const data = await api.listEvents(t.id);
            for (const e of data.events || []) {
              all.push({ ...e, team_name: t.name, team_sport: t.sport });
            }
          } catch {/* ignore */}
        }
        all.sort((a, b) => eventSortKey(a) - eventSortKey(b));
        setEvents(all);
      } else if (activeTeam) {
        const data = await api.listEvents(activeTeam.id);
        setEvents((data.events || []).map((e) => ({ ...e, team_name: activeTeam.name, team_sport: activeTeam.sport })));
      }
    }
    load();
  }, [activeTeam, teams, user.role]);

  const Header = user.role === "guardian" ? WhiteHeader : DarkHeader;
  return (
    <>
      <Header eyebrow="The Playbook" title="The Playbook" subtitle="Every play, every team." />
      <div style={{ padding: "0 18px 24px" }}>
        <TeamLockBanner team={activeTeam} sourceScreen="playbook" />
        {canAdd && activeTeam && events.length > 0 && (
          <button type="button" data-testid="playbook-bulk-edit" onClick={() => setBulkOpen(true)}
            style={{ margin: "4px 0 12px", background: "transparent", border: "1px solid var(--chalk-border)", borderRadius: 999,
              padding: "8px 16px", fontSize: 13, color: "var(--chalk-board)", cursor: "pointer" }}>
            Bulk Erase and Redraw
          </button>
        )}
        {events.length === 0 ? (
          <EmptyState
            icon={<ChalkIllustrationLines />}
            headline="Nothing on the schedule yet."
            body={user.role === "guardian" ? "Plays will show up here as your coach adds them." : "Add a play to get started."}
            ctaLabel={user.role === "guardian" ? null : "Add a play"}
            onCta={tryAdd}
          />
        ) : (
          events.map((e) => (
            <ChalkCard key={e.id} data-testid={`event-${e.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 500, color: "var(--chalk-board)" }}>{e.title}</div>
                  <div style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 4 }}>
                    {humanWhen(e, user)} · {e.location || "TBD"}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <EventWeatherBadge eventId={e.id} />
                  </div>
                </div>
                <SportBadge sport={e.team_sport} />
              </div>
              {e.status === "cancelled" && (
                <div style={{ marginTop: 8, fontSize: 12, color: "var(--chalk-red)", fontWeight: 500 }}>Cancelled</div>
              )}
              {e.status === "delayed" && (
                <div style={{ marginTop: 8, fontSize: 12, color: "#9a6b00", fontWeight: 500 }}>Delayed</div>
              )}
              {canAdd && e.status !== "cancelled" && (
                <button type="button" data-testid={`event-change-${e.id}`} onClick={() => setChanging(e)}
                  style={{ marginTop: 10, background: "transparent", border: "none", padding: 0, color: "var(--green-mid)", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                  Change time, delay or cancel
                </button>
              )}
            </ChalkCard>
          ))
        )}
      </div>

      {/* Floating "+" FAB so coaches can add events even when there are
          already some — the empty-state CTA only shows for the zero case. */}
      {canAdd && activeTeam && (
        <button
          type="button"
          data-testid="playbook-fab-add"
          onClick={tryAdd}
          aria-label="Add a play"
          style={{
            position: "fixed", right: 18, bottom: 90,
            width: 56, height: 56, borderRadius: 28,
            background: "var(--green-mid)", color: "#fff", border: "none",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 12px 28px rgba(46,125,82,0.32)", cursor: "pointer",
            zIndex: 50,
          }}
        >
          <Plus size={26} aria-hidden="true" />
        </button>
      )}

      <EventChangeSheet isOpen={!!changing} event={changing} teamId={activeTeam?.id} teamTimezone={activeTeam?.timezone}
        onDismiss={() => setChanging(null)} onChanged={reloadEvents} />
      <BulkRedrawSheet isOpen={bulkOpen} teamId={activeTeam?.id} events={events}
        onDismiss={() => setBulkOpen(false)} onChanged={reloadEvents} />
      <AddEventSheet
        isOpen={addOpen}
        onDismiss={() => setAddOpen(false)}
        teamId={activeTeam?.id}
        onCreated={() => reloadEvents()}
      />
    </>
  );
}
